// The DIMENSIONAL widgets' pure core — PURE, and a LEAF (imports nothing).
//
// A pie or a ranked bar takes one metric split by one dimension and has to
// answer two questions before it can draw anything:
//
//   1. MAY it draw at all? (the mixed-currency refusal — see below)
//   2. WHICH values, in what order, and what goes in "Other"?
//
// Both live here rather than in the renderers so they are testable and can be
// mutation-checked, and so the pie and the bar cannot drift apart about what
// "top 8" means.

/** One dimension value's figure. `null` = not measured for this value. */
export interface DimensionSlice {
  key: string;
  label: string;
  value: number | null;
}

export interface SlicePlan {
  shown: Array<{ key: string; label: string; value: number }>;
  /** The tail, summed. Null when nothing was left over. */
  other: { key: string; label: string; value: number } | null;
  /** How many dimension values the tail represents — named in the legend. */
  otherCount: number;
}

export const OTHER_SLICE_KEY = '__other__';

/**
 * Rank by value, keep the top N, and roll the rest into "Other".
 *
 * ⚠️ UNMEASURED VALUES ARE DROPPED, NOT ZEROED. A null means the metric was not
 * reported for that dimension value (an impression share Google never gave us,
 * say). A zero-height bar reads as "this location won none of the auction", and
 * a zero-area pie slice reads as nothing at all while still occupying a legend
 * row — both are claims we cannot support. Dropping is the honest rendering; the
 * count of dropped values is reported so the widget can say so.
 *
 * ⚠️ NEGATIVE VALUES ARE DROPPED FOR A PIE-SHAPED READING TOO — a negative slice
 * has no meaningful area. None of the current metrics can go negative, so this
 * is a guard rather than a behaviour; it lives here so it cannot be forgotten if
 * one ever does.
 */
export function planSlices(slices: readonly DimensionSlice[], topN: number): SlicePlan {
  const usable = slices.filter(
    (s): s is DimensionSlice & { value: number } => s.value !== null && s.value >= 0,
  );
  const ranked = [...usable].sort((a, b) => b.value - a.value || a.label.localeCompare(b.label));
  const shown = ranked.slice(0, Math.max(1, topN)).map((s) => ({
    key: s.key,
    label: s.label,
    value: s.value,
  }));
  const tail = ranked.slice(shown.length);
  const otherTotal = tail.reduce((n, s) => n + s.value, 0);
  return {
    shown,
    // ⚠️ Only when the tail actually contributes something. An "Other: 0" slice
    // is invisible on a pie but still takes a legend row and reads as a real
    // category — noise pretending to be information.
    other: tail.length > 0 && otherTotal > 0
      ? { key: OTHER_SLICE_KEY, label: `Other (${tail.length})`, value: otherTotal }
      : null,
    otherCount: tail.length,
  };
}

/** How many of the supplied values had no measurement. */
export function unmeasuredCount(slices: readonly DimensionSlice[]): number {
  return slices.filter((s) => s.value === null).length;
}

export type DimensionalVerdict =
  | { ok: true }
  | { ok: false; reason: string };

/**
 * ⚠️⚠️ THE INTEGRITY GATE. A MONEY metric split by a dimension across a
 * MIXED-CURRENCY scope must never be plotted.
 *
 * Adding an AUD figure to an NZD one produces a number that exists in no
 * currency, and a pie is exactly the shape that hides it: the slices still sum
 * to a tidy 100% and look completely reasonable. Putting that on a client report
 * is the same class of error as the Phase-1 delta refusal, and the same answer
 * applies — say what is wrong and how to fix it, rather than showing something
 * plausible and false.
 *
 * Counts and percentages are currency-agnostic (a click is a click), so they are
 * unaffected and plot normally in a mixed scope.
 */
export function canPlotDimensional(input: {
  format: 'money' | 'count' | 'ratio';
  mixedCurrency: boolean;
  currencies: readonly string[];
  sliceCount: number;
  unavailable?: string;
}): DimensionalVerdict {
  if (input.unavailable) return { ok: false, reason: input.unavailable };
  if (input.format === 'money' && input.mixedCurrency) {
    const list = [...input.currencies].sort().join(' + ');
    return {
      ok: false,
      reason: `This scope covers ${list || 'several currencies'}. Money can’t be split by a dimension across currencies without inventing a total — filter to one currency to see this.`,
    };
  }
  if (input.sliceCount === 0) return { ok: false, reason: 'No data in this range.' };
  return { ok: true };
}

/**
 * A gauge shows a percentage on a 0–100 dial.
 *
 * ⚠️ NULL STAYS NULL — an unmeasured impression share is not a needle at zero,
 * which would read as "you won none of the auction". Only a real measurement
 * moves the dial; everything else renders as "—".
 */
export function clampPercent(value: number | null): number | null {
  if (value === null || !Number.isFinite(value)) return null;
  return Math.min(100, Math.max(0, value));
}

/** Each funnel stage's share of the FIRST stage, for the taper and the labels. */
export function funnelRatios(
  values: readonly (number | null)[],
): Array<{ ratio: number; ofPrevious: number | null }> {
  const first = values[0];
  const base = first !== null && first !== undefined && first > 0 ? first : 0;
  return values.map((v, i) => {
    const prev = i > 0 ? values[i - 1] : null;
    return {
      // A stage we could not measure contributes no width rather than a
      // full-width bar or a zero that reads as a real drop-off.
      ratio: v === null || v === undefined || base === 0 ? 0 : Math.min(1, v / base),
      ofPrevious:
        i === 0 || v === null || v === undefined || prev === null || prev === undefined || prev <= 0
          ? null
          : (v / prev) * 100,
    };
  });
}

/**
 * The dimension the page must fetch a breakdown for — the first dimensional
 * widget's, or null when none exists.
 *
 * ⚠️ ONE dimension per page, deliberately. Two dimensional widgets on different
 * dimensions would mean two aggregations per load; until someone actually wants
 * that, the first one wins and the others render against it. The alternative —
 * a query per widget — turns a cheap feature into a costly one by accident.
 */
export function firstWidgetDimension(
  widgets: ReadonlyArray<{ vizType: string; dimension?: string }>,
): string | null {
  for (const w of widgets) {
    if (w.vizType !== 'pie' && w.vizType !== 'rankedBar') continue;
    if (w.dimension) return w.dimension;
    // A dimensional widget with nothing chosen yet still needs data — the
    // renderer falls back to the dashboard's first dimension, so the fetch has
    // to as well or the widget would sit empty until reconfigured.
    return null;
  }
  return null;
}

/** Whether any widget on the page is a dimensional one at all. */
export function hasDimensionalWidget(
  widgets: ReadonlyArray<{ vizType: string }>,
): boolean {
  return widgets.some((w) => w.vizType === 'pie' || w.vizType === 'rankedBar');
}
